import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import map from 'lodash/map';
import find from 'lodash/find';
import isEmpty from 'lodash/isEmpty';
import { filter } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Col } from 'react-bootstrap';
import HealthIcon from './HealthIcon';
import { KPI_BUTTONS, MAX_SELECTED_KPIS } from './constants';
import clsx from 'clsx';
import { addTrendKpi, removeSelectedKpi } from '../../actions';
import { Checkbox } from 'sharedComponents/Checkbox';
import { SearchBox } from 'sharedComponents/SearchBox';
import { IconCondition } from 'svgIcons/MotionPortalIcons';
import { contractInfoMessages, limitIndications, MAX_INPUT_CHAR_LENGTH } from 'helpers/constants';
import useUserCapability from 'sharedHooks/useUserCapability';
import { setIsDefaultLimitsSelected } from '../../LimitSelector/actions';
import { GeneralButton } from 'sharedComponents/GeneralButton';
import CautionBadge from './CautionBadge';
import { getTranslatedKPINameV2 } from 'helpers/translateHelper';

const renderMinMaxAndAverage = (minValue, maxValue, avgValue, offset, hasLengthSelectedTrendKpi, presion) => {
    const min = minValue || minValue === 0 ? +minValue + +offset : '';
    const max = maxValue || maxValue === 0 ? +maxValue + +offset : '';
    const avg = avgValue || avgValue === 0 ? +avgValue + +offset : '-';
    return <>
        <div className='data-min-max'>
            <span>{hasLengthSelectedTrendKpi ? min !== '' && min?.toFixed(presion) : ''}</span>
            <span>{hasLengthSelectedTrendKpi ? minValue !== null && maxValue !== null ? '/' : '-' : ''}</span>
            <span>{hasLengthSelectedTrendKpi ? max !== '' && max?.toFixed(presion) : ''}</span>
        </div>
        <div className='data-average'>
            <span>{hasLengthSelectedTrendKpi ? avg !== '-' && avg?.toFixed(presion) : ''}</span>
        </div>
    </>;
};

const renderKpiItem = (kpi, isSelected, selectedTrendKpi, userCapabilities, toggleMeasurement, capability, dateSelection, setShowLimitSetting, setClickedConfigKpi) => {
    const hasLengthSelectedTrendKpi = selectedTrendKpi?.length;
    const minValue = hasLengthSelectedTrendKpi && selectedTrendKpi[0]?.minValue;
    const maxValue = hasLengthSelectedTrendKpi && selectedTrendKpi[0]?.maxValue;
    const avgValue = hasLengthSelectedTrendKpi && selectedTrendKpi[0]?.avgValue;
    const offset = hasLengthSelectedTrendKpi && selectedTrendKpi[0]?.offset || 0;
    const presion = hasLengthSelectedTrendKpi && selectedTrendKpi[0]?.precision || 1;
    const signalName = getTranslatedKPINameV2(kpi);
    return (
        <button
            className={clsx('measurement-list-item', isSelected && 'selected-measurement-list-item')}
            onClick={(event) => {
                if (event?.target?.closest?.('.general-dialog-container')) {
                    return;
                }

                if (kpi?.isPlottableToTrendChart) {
                    toggleMeasurement(kpi, isSelected);
                }
            }}
            key={kpi.timeseriesKey}
            type='button'
        >
            <div className='data-command-checkbox'>
                <Checkbox
                    checked={isSelected}
                    disabled={!kpi?.isPlottableToTrendChart}
                />
            </div>
            <div className='data-label'>
                <div className='health-icon'>
                    <HealthIcon kpi={kpi} />
                </div>
                <div className='condition-icon-label'>
                    {userCapabilities?.hasOperationalParametersLimitStatus() &&
                        <div className='condition-icon'>
                            {kpi?.limitStatus ?
                                <IconCondition condition={limitIndications[kpi?.limitStatus]?.iconID} />
                                : null}
                        </div>
                    }
                    <span className='signal-name'>{signalName}</span>
                    <span className='advisory-icon-slot'>
                        {kpi?.advisory ? <CautionBadge advisory={kpi.advisory} /> : null}
                    </span>
                </div>
                {kpi?.unit && <div className='data-label__unit'>{kpi?.unit}</div>}
            </div>
            {userCapabilities?.hasOperationalParametersMinMax() && renderMinMaxAndAverage(minValue, maxValue, avgValue, offset, hasLengthSelectedTrendKpi, presion)}
            <div className='data-value'>{kpi.displayValue ?? '-'}</div>
            <div className='data-command'>
                {map(KPI_BUTTONS, (KpiButton, index) =>
                    <KpiButton
                        kpi={kpi}
                        capability={capability}
                        isSelected={isSelected}
                        dateSelection={dateSelection}
                        key={index}
                        setShowLimitSetting={setShowLimitSetting}
                        setClickedConfigKpi={setClickedConfigKpi}
                    />
                )}
            </div>
        </button>
    );
};

const ParameterSection = (props) => {
    const { t: translate } = useTranslation();
    const { section, capability, selectedTrendKPIs, dateSelection, selectedTrendsData, setShowLimitSetting, setClickedConfigKpi, selectedNotification } = props;
    const userCapabilities = useUserCapability();
    const hasActiveSubscriptionAndCapability = capability?.subscriptionStatus() === contractInfoMessages?.activeSubscription && capability?.hasLimitConfigCapability();
    const [searchboxFilter, setSearchboxFilter] = useState('');
    const dispatch = useDispatch();
    const measurementListBodyRef = useRef(null);

    // Safely derive data so hooks (below) are called unconditionally
    const data = section?.data || [];
    let kpiData = data;
    const serchBoxValue = searchboxFilter.toLowerCase();
    if (serchBoxValue !== '' && serchBoxValue !== null) {
        kpiData = data?.filter((kpi) => {
            return kpi?.label?.toLowerCase().includes(serchBoxValue);
        });
    }
    const toggleMeasurement = (kpi, isSelected) => {
        if (isSelected) {
            dispatch(removeSelectedKpi(kpi));
        }
        else {
            dispatch(addTrendKpi(kpi));
        }
        dispatch(setIsDefaultLimitsSelected(false));
        setTimeout(() => {
            measurementListBodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
        }, 0);
    };

    const handleSearchChange = (searchTerm) => {
        if (searchTerm.length <= 50) {
            setSearchboxFilter(searchTerm);
        }
    };

    const { selectedKpiItems, unselectedKpiItems } = kpiData.reduce((acc, kpi) => {
        if (kpi.timeseriesKey) {
            if (selectedTrendKPIs.includes(kpi.timeseriesKey)) {
                acc.selectedKpiItems.push(kpi);
            } else {
                acc.unselectedKpiItems.push(kpi);
            }
        }
        return acc;
    }, { selectedKpiItems: [], unselectedKpiItems: [] });
    // Combine selected and unselected KPI items to maintain the desired order for rendering
    const sortedKpiData = [...selectedKpiItems, ...unselectedKpiItems];

    // SET TREND KPI FROM NOTIFICATION
    useEffect(() => {
        if (selectedNotification && kpiData?.length > 0 && selectedNotification?.properties?.timeseriesKey) {
            const kpi = selectedNotification?.properties;
            dispatch(addTrendKpi(kpi));
        }
    }, [selectedNotification, kpiData]);

    if (!section?.data || isEmpty(section?.data)) {
        return null;
    }

    return <Col xl lg={12} className={clsx('measurement-list', !userCapabilities?.hasOperationalParametersMinMax() && 'measurement-list-capability')}>
        <div className='measurement-list-title'>
            {/* <div className='measurement-list-buttons'>
                <div>{translate('ABB.Powertrain.Frontend.detailedInformationOperationalParametersTab')}</div>
                {
                    map(SECTION_BUTTONS, (SectionButton, index) => {
                        return <SectionButton section={section} capability={capability} key={index} />;
                    })
                }
            </div> */}
            <div className='signal-group-header-info'>
                <div className='kpisignal__filter__search me-2'>
                    <SearchBox
                        placeholder={translate('ABB.Powertrain.Frontend.searchSignal')}
                        onChange={handleSearchChange}
                        value={searchboxFilter}
                        maxLength={MAX_INPUT_CHAR_LENGTH} />
                </div>
                <span>{selectedTrendKPIs.length}/{MAX_SELECTED_KPIS}{' '}{translate('ABB.Powertrain.Frontend.selected')}</span>
            </div>
            {hasActiveSubscriptionAndCapability ? <div className='limit-settings-button-wrapper'>
                <div className='kpisignal__filter__search'>
                    <GeneralButton
                        type='normal'
                        className='limit-config-Cancel-button'
                        text={translate('ABB.Powertrain.Frontend.limitsSetting')}
                        onClick={() => { setShowLimitSetting(true); }}
                    />
                </div>
            </div> : null}
        </div>
        {<div className='measurement-list-head'>
            <div className='measurement-list-item'>
                <div className='data-command-checkbox'></div>
                <div className='data-label'>{translate('ABB.Powertrain.Frontend.operationalParametersNameLabel')}</div>
                {userCapabilities?.hasOperationalParametersMinMax() && <>
                    <div className='data-min-max'>
                        {translate('ABB.Powertrain.Frontend.operationalParametersMinLabel')}/{translate('ABB.Powertrain.Frontend.operationalParametersMaxLabel')}
                    </div>
                    <div className='data-average'>
                        {translate('ABB.Powertrain.Frontend.operationalParametersAverageLabel')}
                    </div>
                </>}
                <div className='data-value'>{translate('ABB.Powertrain.Frontend.opertaionalParametersLastMeasurementLabel')}</div>
                <div className='data-command'></div>
            </div>
        </div>}
        <div className='measurement-list-body' ref={measurementListBodyRef}>
            {map(sortedKpiData, (kpi) => {
                const isSelected = !!find(selectedTrendKPIs, (selectedTrendKPI) => kpi.timeseriesKey === selectedTrendKPI);
                const selectedTrendKpi = filter(selectedTrendsData, (selectedTrendData) => selectedTrendData?.kpiIdentifier === kpi?.timeseriesKey);
                return renderKpiItem(kpi, isSelected, selectedTrendKpi, userCapabilities, toggleMeasurement, capability, dateSelection, setShowLimitSetting, setClickedConfigKpi);
            })}
        </div>
    </Col>;
};

ParameterSection.propTypes = {
    section: PropTypes.shape({
        data: PropTypes.array,
        sectionId: PropTypes.any
    }),
    capability: PropTypes.object,
    selectedTrendKPIs: PropTypes.array,
    selectedTrendsData: PropTypes.array,
    dateSelection: PropTypes.object,
    setShowLimitSetting: PropTypes.func,
    setClickedConfigKpi: PropTypes.func,
    selectedNotification: PropTypes.shape({
        properties: PropTypes.object,
    })
};

export default ParameterSection;
